import DatePicker from "react-date-picker";
import "react-date-picker/dist/DatePicker.css";
import "react-calendar/dist/Calendar.css";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Box,
  Typography,
} from "@mui/material";
import { MdOutlineAnalytics } from "react-icons/md";
import { useNavigate, useParams } from "react-router-dom";
import { useEventSession } from "../../providers/eventSessions";
import { IEventSession } from "../../providers/eventSessions/eventsSessions.interface";
import { useEvent } from "../../providers/events";
import { IEvent } from "../../providers/events/events.interface";
import { useUser } from "../../providers/users";
import { useTenant } from "../../providers/tenant";
import Loading from "../Loading";
import PageHeader from "../pageHeader";
import { Container } from "./styles";
import { useForm } from "react-hook-form";
import SocketMetricsItem from "../SocketMetricsItem";

const MetricsContainer = () => {
  const { allEvents } = useEvent();
  const { allEventSessions } = useEventSession();
  const { apiLoading } = useUser();
  const {
    getAllSocketMetrics,
    socketPeakData,
    socketHistoryData,
    socketTypesData,
  } = useTenant();
  const { t } = useTranslation();
  const [datePicked, setDatePicked] = useState(new Date());
  const [eventId, setEventId] = useState("");
  const [eventSessionId, setEventSessionId] = useState("");
  const [showMetrics, setShowMetrics] = useState(false);
  const [metricsKey, setMetricsKey] = useState(""); // Para forçar remontagem do componente
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [metricsData, setMetricsData] = useState(null);

  const navigate = useNavigate();
  const { path } = useParams();

  // Efeito para capturar os dados atualizados do provider após o carregamento
  useEffect(() => {
    // Só executa quando não estamos carregando e o showMetrics está ativado
    if (!isLoading && showMetrics) {
      // Atualizar o estado local com os dados mais recentes do provider
      setMetricsData({
        socketPeakData,
        socketHistoryData,
        socketTypesData,
      });
    }
  }, [
    isLoading,
    socketPeakData,
    socketHistoryData,
    socketTypesData,
    showMetrics,
  ]);

  function handleChange(e) {
    const {
      target: { value },
    } = e;
    setEventId(value);
    setEventSessionId("");
    setShowMetrics(false);
    setMetricsData(null);
  }

  function handleChangeEventSession(e) {
    const {
      target: { value },
    } = e;
    setEventSessionId(value);
    setShowMetrics(false);
    setMetricsData(null);
  }

  function handleDateChange(date) {
    setDatePicked(date);
    setShowMetrics(false);
    setMetricsData(null);
  }

  // Formatar data para uso em chaves e logs
  const formatDate = (date) => {
    if (!date) return "null-date";
    return date.toISOString().split("T")[0];
  };

  const onSubmit = async () => {
    if (!eventSessionId) {
      navigate("/events");
      return;
    }

    const selectedSession = allEventSessions.find(
      (session) => session.id === eventSessionId
    );

    if (!selectedSession || !selectedSession.chatRoom) {
      setError("No valid chat room found for this session");
      return;
    }

    setIsLoading(true);
    setError(null);
    setShowMetrics(false);
    setMetricsData(null);

    try {
      // Fazer a requisição diretamente aqui em vez de no componente filho
      const formattedDate = formatDate(datePicked);

      console.log(
        `Fetching socket metrics for room ${selectedSession.chatRoom} on ${formattedDate}`
      );
      await getAllSocketMetrics(selectedSession.chatRoom, formattedDate);

      // Pequeno timeout para garantir que os dados foram processados pelo provider
      setTimeout(() => {
        // Capturar os dados do contexto/provider
        const newMetricsData = {
          socketPeakData: socketPeakData || {},
          socketHistoryData: socketHistoryData || { history: [] },
          socketTypesData: socketTypesData || {},
        };

        console.log("Metrics data fetched:", newMetricsData);

        // Atualizar o estado local com os dados obtidos (mesmo se forem "vazios")
        setMetricsData(newMetricsData);

        // Criar uma chave única para forçar a remontagem do componente
        const newKey = `metrics-${
          selectedSession.chatRoom
        }-${formattedDate}-${Date.now()}`;
        setMetricsKey(newKey);
        setShowMetrics(true);
        setIsLoading(false);
      }, 100);
    } catch (err) {
      console.error("Error fetching socket metrics:", err);
      setError("Failed to load metrics. Please try again.");
      setIsLoading(false);
    }
  };

  const {
    register,
    handleSubmit,
    formState: { errors: formErrors },
  } = useForm();

  // Verificar se a sala possui um chatRoom
  const selectedSession = eventSessionId
    ? allEventSessions.find((session) => session.id === eventSessionId)
    : null;

  const hasValidRoom = selectedSession && selectedSession.chatRoom;

  return (
    <Container>
      <PageHeader
        title={"WebSocket Analytics"}
        description={"Connection metrics and analytics"}
        icon={<MdOutlineAnalytics />}
      />

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="datePickerContainer custom-select"
      >
        <DatePicker
          className="datePicker"
          onChange={handleDateChange}
          value={datePicked}
          format="yyyy-MM-dd"
          clearIcon={null}
          maxDate={new Date()}
          required
        />
        <FormControl id="select-label">
          <InputLabel id="demo-simple-select-events">{t("events")}</InputLabel>
          <Select
            labelId="demo-simple-select-events"
            id="demo-simple-select-events"
            value={eventId}
            required
            label={t("events")}
            placeholder="Choose an event"
            onChange={handleChange}
          >
            {allEvents &&
              allEvents.map((e) => (
                <MenuItem key={e.id} value={e.id}>
                  {e.name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <FormControl id="select-label">
          <InputLabel id="demo-simple-select-events">Event session</InputLabel>
          <Select
            labelId="demo-simple-select-events"
            id="demo-simple-select-events"
            value={eventSessionId}
            sx={{ mt: 0 }}
            required
            label="Event session"
            placeholder="Choose a session"
            onChange={handleChangeEventSession}
            disabled={!eventId}
          >
            {eventId &&
              allEventSessions
                .filter((e) => e.eventId === eventId)
                .map((e) => (
                  <MenuItem key={e.id} value={e.id}>
                    {e.name}
                  </MenuItem>
                ))}
          </Select>
        </FormControl>
        <Button
          type="submit"
          variant="contained"
          disabled={!eventId || !eventSessionId || !datePicked || isLoading}
        >
          {isLoading ? "Loading..." : "Search"}
        </Button>
      </form>

      {isLoading ? (
        <Loading />
      ) : error ? (
        <Box mt={4} textAlign="center">
          <Typography variant="h6" color="error">
            {error}
          </Typography>
        </Box>
      ) : showMetrics && selectedSession && metricsData ? (
        hasValidRoom ? (
          <div key={metricsKey}>
            <SocketMetricsItem
              session={selectedSession}
              date={datePicked}
              metricsData={metricsData}
            />
          </div>
        ) : (
          <Box mt={4} textAlign="center">
            <Typography variant="h6" color="error">
              No Room ID found for this session. WebSocket metrics are not
              available.
            </Typography>
          </Box>
        )
      ) : showMetrics && !metricsData ? (
        <Box mt={4} textAlign="center">
          <Typography variant="body1" color="warning">
            No data found for the selected criteria. Try another date or
            session.
          </Typography>
        </Box>
      ) : (
        <Box mt={4} textAlign="center">
          <Typography variant="body1" color="textSecondary">
            Select an event, session, and date to view WebSocket connection
            metrics.
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default MetricsContainer;
